import { useDeepCompareMemo } from "@react-hookz/web";
import { isEmpty } from "lodash-es";
import { useMemo } from "react";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import { notEmpty } from "../../../utils/not-empty";
import { SupabaseRecipientService } from "../../recipients/server/supabase-recipient-service";
import { getToTarget } from "../../to/to-target";

import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type {
  PreloadedPreviewRows,
  PreviewMomentData,
} from "../component/buttons/preview-button/preview-dialog/types";

export type MomentRecipientsClassify =
  | {
      type: "channel_notify";
      data: {
        segment_name: string;
        channel_name: string | null;
      };
    }
  | {
      type: "channel";
      data: {
        channel_name: string | null;
      };
    }
  | {
      type: "notify";
      data: {
        segment_name: string;
        recipients: string[];
        cc: string[];
        bcc: string[];
      };
    }
  | {
      type: "connection";
      data: {
        segment_name: string;
        connection_type: string;
        cc: string[];
        bcc: string[];
      };
    }
  | {
      type: "recipients";
      data: {
        segment_name: string;
        cc: string[];
        bcc: string[];
      };
    }
  | {
      type: "unknown";
      data: null;
    };

interface Data {
  channel: PreviewMomentData["channel"];
  additional_recipients: PreviewMomentData["additional_recipients"];
  target_connection_type: PreviewMomentData["target_connection_type"];
  cc_bcc: PreviewMomentData["cc_bcc"];
  segment_operation: NonNullable<PreviewMomentData["segment"]>["operation"] | null;
  base_segment_name: NonNullable<PreviewMomentData["segment"]>["name"] | null;
  target_channel: PreviewMomentData["target_channel"];
  target_manager: PreviewMomentData["target_manager"];
  only_additional_recipients: PreviewMomentData["only_additional_recipients"];
  notify_channel: PreviewMomentData["notify_channel"];
  /** The recipients the Communication names, when they came with it. */
  recipients?: PreloadedPreviewRows["recipients"];
}

/** What to call a Communication's segment on screen, unnamed and unset ones included. */
export const segmentDisplayName = (
  segment_operation: Data["segment_operation"],
  base_segment_name: Data["base_segment_name"],
): string =>
  !segment_operation
    ? "Not Set"
    : base_segment_name && !isEmpty(base_segment_name)
      ? base_segment_name
      : "Ad-Hoc Segment";

/** Who a channel or notify Communication is sent to, as it is named on screen. */
export const notifiedDisplayName = (
  classifyData: Extract<
    MomentRecipientsClassify,
    { type: "channel_notify" | "channel" | "notify" }
  >,
): string => {
  if (classifyData.type !== "notify")
    return classifyData.data.channel_name ? `#${classifyData.data.channel_name}` : "Not Set";

  const { recipients } = classifyData.data;
  if (recipients.length === 0) return "Not Set";
  return recipients.length > 1 ? `${recipients.length} recipients` : recipients[0]!;
};

export function useMomentRecipientsClassify({
  segment_operation,
  base_segment_name,
  cc_bcc,
  target_connection_type,
  additional_recipients,
  channel,
  target_channel,
  target_manager,
  only_additional_recipients,
  notify_channel,
  recipients,
}: Data): Omit<UseSupabaseLoaderState<MomentRecipientsClassify>, "reload"> {
  const segment_name = segmentDisplayName(segment_operation, base_segment_name);

  const targetType = getToTarget({
    target_channel: target_channel ?? false,
    target_manager: target_manager ?? false,
    only_additional_recipients: only_additional_recipients ?? false,
    notify_channel: notify_channel ?? false,
    target_connection_type,
  });

  const { static_cc, static_bcc } = useDeepCompareMemo(() => {
    const cc = cc_bcc?.cc ?? [];
    const bcc = cc_bcc?.bcc ?? [];
    return {
      static_cc: cc.filter((recipient) => recipient.type === "static"),
      static_bcc: bcc.filter((recipient) => recipient.type === "static"),
    };
  }, [cc_bcc]);

  const {
    data: recipient_data,
    loading,
    error,
  } = useSupabaseLoader(
    async ({ supabase, account_id }): Promise<PreloadedPreviewRows["recipients"]> => {
      if (!channel) return [];
      if (recipients) return recipients;

      const response = await new SupabaseRecipientService(supabase).getForChannel(
        account_id,
        channel,
      );

      if (response.error) throw response.error;

      return response.data;
    },
    [channel, recipients],
  );

  const display_values = useMemo(() => {
    if (!recipient_data)
      return { cc_emails: [], bcc_emails: [], additional_recipients_display: [] };

    const cc_emails = recipient_data
      .filter((recipient) => static_cc.some((x) => x.id === recipient.id))
      .map((x) => x.display)
      .filter(notEmpty);
    const bcc_emails = recipient_data
      .filter((recipient) => static_bcc.some((x) => x.id === recipient.id))
      .map((x) => x.display)
      .filter(notEmpty);
    const additional_recipients_display = recipient_data
      .filter((recipient) => (additional_recipients ?? []).includes(recipient.id))
      .map((x) => x.display)
      .filter(notEmpty);

    return { cc_emails, bcc_emails, additional_recipients_display };
  }, [recipient_data, static_cc, static_bcc, additional_recipients]);

  const data = useMemo(() => {
    if (targetType.toTarget === "channel") {
      if (targetType.notify_channel) {
        return {
          type: "channel_notify" as const,
          data: {
            segment_name,
            channel_name: display_values.additional_recipients_display[0]!,
          },
        };
      }

      return {
        type: "channel" as const,
        data: { channel_name: display_values.additional_recipients_display[0]! },
      };
    }

    if (targetType.toTarget === "notify") {
      return {
        type: "notify" as const,
        data: {
          segment_name,
          recipients: display_values.additional_recipients_display,
          cc: display_values.cc_emails,
          bcc: display_values.bcc_emails,
        },
      };
    }

    if (targetType.toTarget === "manager") {
      return {
        type: "connection" as const,
        data: {
          segment_name,
          connection_type: "manager",
          cc: display_values.cc_emails,
          bcc: display_values.bcc_emails,
        },
      };
    }

    if (targetType.toTarget === "connection") {
      return {
        type: "connection" as const,
        data: {
          segment_name,
          connection_type: target_connection_type ?? "unknown",
          cc: display_values.cc_emails,
          bcc: display_values.bcc_emails,
        },
      };
    }

    if (targetType.toTarget === "employee") {
      return {
        type: "recipients" as const,
        data: {
          segment_name,
          cc: display_values.cc_emails,
          bcc: display_values.bcc_emails,
        },
      };
    }

    return { type: "unknown" as const, data: null };
  }, [targetType, segment_name, display_values, target_connection_type]);

  if (loading || error) return { data: null, loading, error };

  return { data, loading: false, error: null };
}
